import type { BackofficeOperator } from '@repo/sdk/gen'
import { zBackofficeOperator } from '@repo/sdk/zod'
import { getErrorCode } from '@repo/ui/utils'
import type { TKey } from '@/lib/i18n'

export type OperatorStatus = BackofficeOperator['status']

/**
 * The four states, read off the contract rather than retyped: the filter's
 * options come from here, and a fifth value reaches the maps below as a
 * compile error.
 */
export const OPERATOR_STATUSES = zBackofficeOperator.shape.status.options

export const OPERATOR_STATUS_LABEL_KEYS = {
	invited: 'operator-operators:status.invited',
	expired: 'operator-operators:status.expired',
	active: 'operator-operators:status.active',
	disabled: 'operator-operators:status.disabled'
} as const satisfies Record<OperatorStatus, TKey>

export type OperatorAction =
	| 'resend'
	| 'change_email'
	| 'revoke'
	| 'deactivate'
	| 'reactivate'
	| 'remove'

/**
 * Status → what a row offers. Total by construction, so a status the contract
 * adds is a compile error rather than a row offering whatever happened to
 * match.
 *
 * An invitation still waiting (or run out) is acted on as an invitation:
 * resent, corrected, taken back. An operator who arrived is barred, restored
 * or removed. Removal is reached only through an account that exists, and
 * revocation only through one that does not, so the two never sit on one row.
 */
const ACTIONS_BY_STATUS = {
	invited: ['resend', 'change_email', 'revoke'],
	expired: ['resend', 'change_email', 'revoke'],
	active: ['deactivate', 'remove'],
	disabled: ['reactivate', 'remove']
} as const satisfies Record<OperatorStatus, readonly OperatorAction[]>

/**
 * What one row offers. The caller's own row offers nothing: the server refuses
 * every act on oneself (`operator_self_action`), and a menu of commands that
 * all answer `409` is worse than none.
 *
 * This hides; it does not authorize. The server refuses regardless.
 */
export function operatorActions(
	operator: Pick<BackofficeOperator, 'isSelf' | 'status'>
): readonly OperatorAction[] {
	return operator.isSelf ? [] : ACTIONS_BY_STATUS[operator.status]
}

/** The word a confirmation calls somebody by: name, then address. */
export function operatorLabel(operator: BackofficeOperator) {
	return operator.name ?? operator.email
}

/**
 * The refusals a dialog states in place rather than in a toast, because each
 * is about the act just attempted and has its own remedy. `null` for anything
 * else, which the caller reports as a generic failure.
 */
export function operatorRefusalKey(error: unknown) {
	const code = getErrorCode(error)
	return code !== undefined && code in REFUSAL_KEYS
		? REFUSAL_KEYS[code as keyof typeof REFUSAL_KEYS]
		: null
}

const REFUSAL_KEYS = {
	already_exists: 'operator-operators:refusals.already_exists',
	operator_self_action: 'operator-operators:refusals.self_action',
	last_active_operator: 'operator-operators:refusals.last_active_operator'
} as const satisfies Record<string, TKey>
